<script lang="ts" setup>
import { doc, setDoc } from "firebase/firestore";
import { Subscription } from "rxjs";
import { EvaluationTest } from "types/EvaluationTest";
import { EvaluationTestRuns } from "~/classes/models/evaluations/evaluation_test_run.model";
import { EvaluationsService } from "~/classes/models/evaluations/evaluations.service";
import { EvaluationTestStatuses } from "~/types/enums/EvaluationTestStatus";

const props = defineProps({
  evaluationTestId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["onSave"]);

const documentId = inject("documentId") as any | undefined;

const evaluationTest = ref<EvaluationTest | undefined>(undefined);

const isInitialized = ref(false);
const initialize = async () => {
  isInitialized.value = false;
  try {
    evaluationTest.value = await useReportedProblems().fetchEvaluationTest(
      props.evaluationTestId
    );
  } catch (error) {}

  isInitialized.value = true;
};

initialize();

const isSaving = ref(false);

const saveTest = async () => {
  if (evaluationTest.value == undefined) {
    useBaseToast("Error saving test", "error");
    return;
  }

  isSaving.value = true;

  await useReportedProblems().updateEvaluationTest(evaluationTest.value);

  emit("onSave", evaluationTest.value);
  useBaseToast("Test saved!", "success");
  isSaving.value = false;
};

const status = computed(() => {
  return;
});

const { evaluationMetricsStream } = useEvaluationMetrics();

const selectedTab = ref<"summary" | "testing" | "nlp">("summary");
const selectTab = (tab: "summary" | "testing" | "nlp") => {
  selectedTab.value = tab;
};

const metricName = computed(() => {
  const matchingMetric = evaluationMetricsStream.data.value.find(
    (metric) => metric.id == evaluationTest.value?.metricId
  );

  return matchingMetric?.name ?? "";
});

// NLP Functions
const nlpResponse = ref<any>();
const isParsing = ref(false);
const parse = async () => {
  if (!evaluationTest.value || !evaluationTest.value.documentText) return;

  isParsing.value = true;

  const response = await useFetch(`/api/sys-admin/nlp/evaluate`, {
    method: "POST",
    headers: await useApiHeaders(),
    body: {
      text: evaluationTest.value.documentText,
    },
  });

  nlpResponse.value = response.data.value;

  isParsing.value = false;
};

const isAnalyzingWithGoogle = ref(false);
const googleData = ref<any>();
const analyzeWithGoogle = async () => {
  if (!evaluationTest.value || !evaluationTest.value.documentText) return;

  isAnalyzingWithGoogle.value = true;

  const response = await useFetch("/api/sys-admin/nlp/google/analyze", {
    method: "POST",
    headers: await useApiHeaders(),
    body: {
      text: evaluationTest.value.documentText,
    },
  });

  googleData.value = response.data.value;

  isAnalyzingWithGoogle.value = false;
};

const evaluationTestRuns = ref<EvaluationTestRuns>([]);
const evaluationTestRunsSubscription = ref<Subscription>();

watch(isInitialized, () => {
  evaluationTestRunsSubscription.value?.unsubscribe();

  evaluationTestRunsSubscription.value =
    EvaluationsService.streamEvaluationTestRuns({
      metricId: evaluationTest.value?.metricId,
    }).subscribe((runs) => {
      evaluationTestRuns.value = runs;
    });
});

onUnmounted(() => {
  evaluationTestRunsSubscription.value?.unsubscribe();
});
const selectedRunId = ref<string | undefined>(undefined);
const isCopyingTest = ref(false);
const copyTest = async () => {
  if (!evaluationTest.value) return;

  if (selectedRunId.value == undefined) return;

  const selectedRun = evaluationTestRuns.value.find(
    (run) => run.id == selectedRunId.value
  );

  if (!selectedRun) return;

  isCopyingTest.value = true;

  const response = await useCopyTestToRun({
    evaluationTest: evaluationTest.value,
    evaluationTestRun: selectedRun,
  });

  const db = useFirestore();
  const docRef = doc(
    db,
    `/evaluationTestRuns/${selectedRunId.value}/evaluationTests/${evaluationTest.value.id}`
  );

  await setDoc(docRef, response);

  isCopyingTest.value = false;
};
</script>

<template>
  <div
    class="ml-4 bg-surface shadow rounded transition-opacity p-4 flex flex-col h-full"
  >
    <h2 class="text-h4 w-full flex flex-row justify-between">
      <span>Review Reported Problem</span>
    </h2>
    <div
      class="flex flex-row items-center my-2 w-full border border-light-default"
    >
      <div
        class="p-4 grow text-center border-r border-light-default cursor-pointer hover:bg-light-default"
        :class="{
          'bg-primary-default bg-opacity-20': selectedTab == 'summary',
        }"
        @click="selectTab('summary')"
      >
        Summary
      </div>
      <div
        class="p-4 grow text-center cursor-pointer hover:bg-light-default border-r"
        :class="{
          'bg-primary-default bg-opacity-20': selectedTab == 'testing',
        }"
        @click="selectTab('testing')"
      >
        Testing
      </div>
      <div
        class="p-4 grow text-center cursor-pointer hover:bg-light-default"
        :class="{
          'bg-primary-default bg-opacity-20': selectedTab == 'nlp',
        }"
        @click="selectTab('nlp')"
      >
        NLP
      </div>
    </div>
    <div class="grow flex flex-col min-h-0">
      <div v-if="selectedTab == 'summary'" class="grow flex flex-col min-h-0">
        <span
          v-if="isInitialized == true && evaluationTest == undefined"
          class="text text-danger-default my-2"
        >
          Error loading reported problem
        </span>
        <BaseLoading v-if="isInitialized == false" />
        <div
          v-if="isInitialized && evaluationTest"
          class="grow min-h-0 mt-2 flex flex-col"
        >
          <div class="grow min-h-0 flex flex-col overflow-auto">
            <span class="mb-2 text-[18px] font-bold">
              Reported:
              {{ useBasicDateTimeFormatter(evaluationTest.createdAtTimestamp) }}
            </span>
            <BaseFormComponent>
              <template #label> Name </template>
              <BaseTextInput v-model="evaluationTest.name" class="w-full" />
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Description </template>
              <BaseTextArea
                v-model="evaluationTest.description"
                class="w-full h-[100px]"
              />
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Is Control? </template>
              <BaseToggle v-model="evaluationTest.isControl" />
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Metric </template>
              <BaseSelectInput
                v-model="evaluationTest.metricId"
                :options="evaluationMetricsStream"
                value-prop="id"
                label="name"
                :can-clear="false"
                :searchable="true"
              />
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Run </template>
              <div class="flex flex-row items-center gap-2" v-auto-animate>
                <BaseSelectInput
                  v-model="selectedRunId"
                  placeholder="Add to test run..."
                  :options="evaluationTestRuns"
                  value-prop="id"
                  label="label"
                  :can-clear="false"
                  :searchable="true"
                />
                <BaseTextButton
                  v-if="selectedRunId"
                  :show-spinner="isCopyingTest"
                  @click="copyTest"
                >
                  Copy
                </BaseTextButton>
              </div>
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Tags </template>
              <EvaluationTestRunsTagSelect v-model="evaluationTest.tags" />
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Status </template>
              <BaseSelectInput
                v-model="evaluationTest.status"
                :options="EvaluationTestStatuses"
                value-prop="value"
                label="label"
                :can-clear="false"
              />
            </BaseFormComponent>
            <BaseFormComponent>
              <template #label> Jira Ticket(s) </template>
              <JiraSelect
                v-model="evaluationTest.jiraIdentifiers"
                :allow-user-entered="true"
                mode="tags"
              />
            </BaseFormComponent>

            <DocumentProblemReviewTestingSelectedTokens
              v-if="documentId"
              :evaluation-test="evaluationTest"
              :document-id="documentId"
              @save="saveTest"
            />
            <TransitionsCollapsible>
              <div
                v-if="evaluationTest.documentText"
                class="mt-4 p-2 border-2 border-dashed"
              >
                {{ evaluationTest.documentText }}
              </div>
            </TransitionsCollapsible>

            <DocumentProblemReviewTree
              v-if="documentId"
              :problem="evaluationTest.originalReportedProblem.problem"
              class="mt-4 rounded p-2 border-2"
            />
          </div>

          <div class="py-4">
            <BaseButton :show-spinner="isSaving" @click="saveTest">
              Save Test
            </BaseButton>
          </div>
        </div>
      </div>
      <DocumentProblemReviewTestingContainer
        v-if="selectedTab == 'testing' && evaluationTest"
        class="grow flex flex-col min-h-0"
        :evaluation-test="evaluationTest"
        @save-test="saveTest"
      />
      <DocumentProblemReviewNLPContainer
        v-if="selectedTab == 'nlp' && evaluationTest"
        :evaluation-test="evaluationTest"
        :metric-name="metricName"
        :nlp-response="nlpResponse"
        :google-data="googleData"
        :is-parsing="isParsing"
        :is-analyzing-with-google="isAnalyzingWithGoogle"
        @parse="parse"
        @analyze-with-google="analyzeWithGoogle"
      />
    </div>
  </div>
</template>

<style scoped></style>
